<template>
  <div class="container-fluid">
    <!-- Row 1: Bookmarked Sensors & Latest Communication -->
    <div class="row mb-4 h-80 min-h-96">
      <div class="col-md-6 mb-4 mb-md-0">
        <div class="card h-100">
          <div class="card-header d-flex justify-content-between align-items-center">
            <span>Bookmarked Sensors</span>
            <small class="text-black mr-2">{{ bookmarkedReadings.length }} total</small>
          </div>
          <div class="card-body p-0 scrollable-table" style="max-height: 300px;">
            <!-- Loading Skeleton -->
            <div v-if="bookmarkLoading" class="p-3">
              <div class="skeleton-loader mb-2" style="height: 40px; border-radius: 4px;"></div>
              <div class="skeleton-loader mb-2" style="height: 40px; border-radius: 4px;"></div>
              <div class="skeleton-loader mb-2" style="height: 40px; border-radius: 4px;"></div>
            </div>
            
            <!-- Empty State -->
            <div v-else-if="bookmarkedReadings.length === 0" class="d-flex justify-content-center align-items-center p-3" style="height: 15vh;">
              <div class="text-center">
                <p class="text-[var(--color-txt)] mb-0"
                   data-help="You can bookmark sensors by clicking on the icon next to a sensor on the
                    <a @click='goToMyView' style='cursor: pointer'>My View</a> page.
                    <img src='help/bookmark.png' alt='Bookmark help' class='img-fluid sm-help-img'/>"
                >
                  No bookmarked sensors found
                </p>
                 <small class="text-[var(--color-txt)] mr-2">Bookmark sensors to see them here</small>
              </div>
            </div>
            
            <!-- Data Table -->
            <div v-else>
              <table class="custom-table mb-0">
                <thead>
                  <tr class="text-center">
                    <th>Sensor Name</th>
                    <th>Sensor Model</th>
                    <th>Gateway Name</th>
                    <th>Last Reading</th>
                    <th>Last Communication</th>
                  </tr>
                </thead>
                <tbody>
                <tr
                    v-for="(row, i) in paginatedBookmarkedReadings"
                    :key="`${row.sensor_id}-${i}`"
                    class="reading-row"
                    @click="click(row.sensor_id, row.gateway_id, row.system_group_id, row)"
                >
                  <td>{{ row.sensorname ?? '' }}</td>
                  <td>{{ row.sensormodel ?? '' }}</td>
                  <td>{{ row.gatewayname ?? '' }}</td>
                  <td>{{ getMathCol(row) }}</td>
                  <td>{{ datetimeConversion(row.receive_datetime) ?? '' }}</td>
                </tr>
                </tbody>
              </table>
              
              <!-- Pagination Controls -->
              <div v-if="bookmarkedReadings.length > bookmarkPageSize" class="p-2 border-top d-flex justify-content-between align-items-center">
                <small class="text-[var(--color-txt)]">
                  Showing {{ (bookmarkCurrentPage - 1) * bookmarkPageSize + 1 }} -
                  {{ Math.min(bookmarkCurrentPage * bookmarkPageSize, bookmarkedReadings.length) }}
                  of {{ bookmarkedReadings.length }}
                </small>
                <div class="btn-group btn-group-sm" role="group">
                  <button
                    class="btn btn-outline-secondary"
                    :disabled="bookmarkCurrentPage === 1"
                    @click="bookmarkCurrentPage = Math.max(1, bookmarkCurrentPage - 1)"
                  >
                    Previous
                  </button>
                  <button
                    class="btn btn-outline-secondary"
                    :disabled="bookmarkCurrentPage * bookmarkPageSize >= bookmarkedReadings.length"
                    @click="bookmarkCurrentPage++"
                  >
                    Next
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="col-md-6">
        <div class="card h-100">
          <div class="card-header d-flex justify-content-between align-items-center">
            <span>Latest Communication</span>
            <small class="text-black mr-2">{{ lastReadings.length }} total</small>
          </div>
          <div class="card-body p-0 scrollable-table" style="max-height: 300px;">
            <!-- Loading Skeleton -->
            <div v-if="lastLoading" class="p-3">
              <div class="skeleton-loader mb-2" style="height: 40px; border-radius: 4px;"></div>
              <div class="skeleton-loader mb-2" style="height: 40px; border-radius: 4px;"></div>
              <div class="skeleton-loader mb-2" style="height: 40px; border-radius: 4px;"></div>
            </div>
            
            <!-- Empty State -->
            <div v-else-if="lastReadings.length === 0" class="d-flex justify-content-center align-items-center p-3" style="height: 15vh;">
              <div class="text-center">
                <p class="mb-2">No sensor readings found yet</p>
                <small class="text-[var(--color-txt)]">New readings will appear when available</small>
                <svg width="24" height="24" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path class="spinner_6KQt" d="M12,1A11,11,0,1,0,23,12,11,11,0,0,0,12,1Zm0,20a9,9,0,1,1,9-9A9,9,0,0,1,12,21Z" transform="translate(12, 12) scale(0)"/></svg>
              </div>
            </div>
            
            <!-- Data Table -->
            <div v-else>
              <table class="custom-table mb-0">
                <thead>
                  <tr class="text-center">
                    <th>Sensor Name</th>
                    <th>Sensor Model</th>
                    <th>Gateway Name</th>
                    <th>Last Reading</th>
                    <th>Last Communication</th>
                  </tr>
                </thead>
                <tbody>
                <tr
                    v-for="(row, i) in paginatedLastReadings"
                    :key="`${row.sensor_id}-${i}`"
                    class="reading-row"
                    @click="click(row.sensor_id, row.gateway_id, row.system_group_id, row)"
                >
                  <td>{{ row.sensorname ?? 'Not Set' }}</td>
                  <td>{{ row.sensormodel ?? 'Not Set' }}</td>
                  <td>{{ row.gatewayname ?? 'Not Set' }}</td>
                  <td>{{ getMathCol(row) }}</td>
                  <td>{{ datetimeConversion(row.receive_datetime) ?? '' }}</td>
                </tr>
                </tbody>
              </table>
              
              <!-- Pagination Controls -->
              <div v-if="lastReadings.length > lastPageSize" class="p-2 border-top d-flex justify-content-between align-items-center">
                <small class="text-[var(--color-txt)]">
                  Showing {{ (lastCurrentPage - 1) * lastPageSize + 1 }} -
                  {{ Math.min(lastCurrentPage * lastPageSize, lastReadings.length) }}
                  of {{ lastReadings.length }}
                </small>
                <div class="btn-group btn-group-sm" role="group">
                  <button
                    class="btn btn-outline-secondary"
                    :disabled="lastCurrentPage === 1"
                    @click="lastCurrentPage = Math.max(1, lastCurrentPage - 1)"
                  >
                    Previous
                  </button>
                  <button
                    class="btn btn-outline-secondary"
                    :disabled="lastCurrentPage * lastPageSize >= lastReadings.length"
                    @click="lastCurrentPage++"
                  >
                    Next
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Row 2: Communication Status & Map -->
    <div class="row">
      <div class="col-md-5 mb-4 mb-md-0">
        <div class="card h-100">
          <div class="card-header d-flex justify-content-between align-items-center">
            <span>Communication Counts for {{ format(new Date(), 'MMMM yyyy') }}</span>
            <small class="text-black mr-2">{{ currentCommUsage.length }} gateways</small>
          </div>
          <!-- The scrollable-table class is applied here -->
          <div class="card-body p-0 scrollable-table" style="max-height: 525px;">
            <!-- Loading Skeleton -->
            <div v-if="commLoading" class="p-3">
              <div class="skeleton-loader mb-2" style="height: 40px; border-radius: 4px;"></div>
              <div class="skeleton-loader mb-2" style="height: 40px; border-radius: 4px;"></div>
              <div class="skeleton-loader mb-2" style="height: 40px; border-radius: 4px;"></div>
            </div>
            
            <!-- Data Table -->
            <table v-else class="custom-table mb-0">
              <thead>
              <tr>
                <th>Gateway</th>
                <th>Name</th>
                <th>Uplink</th>
                <th>Downlink</th>
              </tr>
              </thead>
              <tbody>
              <tr v-for="gateway in currentCommUsage" :key="gateway.gateway_id">
                <td>{{ gateway.gateway_name }}</td>
                <td>{{ gateway.name }}</td>
                <td>{{ gateway.downlink }}</td>
                <td>{{ gateway.uplink }}</td>
              </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <div class="col-md-7">
        <div class="card h-100">
          <div class="card-header d-flex justify-content-between align-items-center">
            <span>Active Gateways</span>
            <small class="text-black mr-2">{{ gatewayCount }} total</small>
          </div>
          <div class="card-body">
            <gateway-map @update-marker-count="n => gatewayCount = n" />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import GatewayMap from '@/components/shared/charts/GatewayMap'
import {computed, onMounted, ref, watch} from 'vue'
import { useStore } from 'vuex'
import { useRouter } from 'vue-router'
import { format } from 'date-fns'
import { byName } from '@/services/sortByName'

export default {
  components: {
    GatewayMap
  },
  name: "Home",
  setup() {
    const store = useStore();
    const router = useRouter();
    const listType = "group_gateway_sensor";
    
    // Pagination state
    const bookmarkCurrentPage = ref(1);
    const lastCurrentPage = ref(1);
    const bookmarkPageSize = ref(10);
    const lastPageSize = ref(10);
    const gatewayCount = ref(0);
    
    // Loading states
    const commLoading = ref(false);
    const bookmarkLoading = computed(() => store.state.user.bookmarkedReadingsLoading);
    const lastLoading = computed(() => store.state.user.lastReadingsLoading);
    const bookmarkedReadings = computed(() => store.state.user.bookmarkedReadings.filter(r => !r.deleted));
    const lastReadings = computed(() => store.state.user.lastReadings);
    const currentCommUsage = computed(() => [...(store.state.user.communicationCounts || [])].sort(byName(g => g.gateway_name)));

    onMounted(async () => {
      // Load the complete MyView data structure (groups, gateways, sensors)
      await store.dispatch("group/findAllForMe", {listType});
      
      // Load gateway data for the map
      await store.dispatch('gateway/findAllForMe');
      
      // Load communication counts with lazy loading
      await loadCommunicationCounts();
      
      // Load bookmarked readings with pagination
      await loadBookmarkedReadings();
      
      // Load last readings with pagination
      await loadLastReadings();
    });

    // Watch for data changes and reset pagination
    watch(() => store.state.user.bookmarkedReadings, () => {
      bookmarkCurrentPage.value = 1;
    });

    watch(() => store.state.user.lastReadings, () => {
      lastCurrentPage.value = 1;
    });

    // Lazy loading for communication counts
    const loadCommunicationCounts = async () => {
      commLoading.value = true;
      try {
        await store.dispatch('user/fetchCommunicationCounts');
      } catch (error) {
        console.error('Failed to load communication counts:', error);
      } finally {
        commLoading.value = false;
      }
    };

    // Load bookmarked readings with error handling
    const loadBookmarkedReadings = async () => {
      try {
        await store.dispatch('user/fetchBookmarkedReadings');
      } catch (error) {
        console.error('Failed to load bookmarked readings:', error);
      }
    };

    // Load last readings with error handling
    const loadLastReadings = async () => {
      try {
        await store.dispatch('user/getLastReadings');
      } catch (error) {
        console.error('Failed to load last readings:', error);
      }
    };

    // Computed properties for pagination
    const paginatedBookmarkedReadings = computed(() => {
      const start = (bookmarkCurrentPage.value - 1) * bookmarkPageSize.value;
      const end = start + bookmarkPageSize.value;
      return bookmarkedReadings.value.slice(start, end);
    });

    const paginatedLastReadings = computed(() => {
      const start = (lastCurrentPage.value - 1) * lastPageSize.value;
      const end = start + lastPageSize.value;
      return store.state.user.lastReadings.slice(start, end);
    });

    const click = async (sensorId, gatewayId, groupId, row = {}) => {
        const listType = "group_gateway_sensor";
        
        // Check multiple possible paths for the MyView data tree
        let groups = store.state.MyView.data?.group_gateway_sensor || store.state.MyView.group_gateway_sensor || [];

        if (groups.length === 0) {
          await store.dispatch("group/findAllForMe", {listType});
          groups = store.state.MyView.data?.group_gateway_sensor || store.state.MyView.group_gateway_sensor || [];
        }

        // Normalize IDs from arguments or the row object, checking all possible keys
        let sId = sensorId || row.sensor_id || row.sensorid || row.id || row.sensor_id;
        let gId = gatewayId || row.gateway_id || row.gatewayid || row.id || row.gateway_id;
        let grpId = groupId || row.system_group_id || row.group_id || row.groupid || row.id;

        // Recovery logic: If IDs are missing, search the MyView tree to find the hierarchy
        if (!grpId || !sId || !gId) {
          if (sId && (!gId || !grpId)) {
            for (const group of groups) {
              // Search all array properties for gateways (gateways, items, children, etc.)
              const gateways = Object.values(group).find(val => Array.isArray(val)) || [];
              for (const gateway of gateways) {
                const sensors = Object.values(gateway).find(val => Array.isArray(val)) || [];
                if (sensors.some(s => (s.sensorid || s.sensor_id || s.id) == sId)) {
                  if (!gId) gId = gateway.gatewayid || gateway.gateway_id || gateway.id;
                  if (!grpId) grpId = group.groupid || group.group_id || group.system_group_id || group.id;
                  break;
                }
              }
            }
          } else if (gId && (!grpId || !sId)) {
            for (const group of groups) {
              const gateways = Object.values(group).find(val => Array.isArray(val)) || [];
              const foundGateway = gateways.find(g => (g.gatewayid || g.gateway_id || g.id) == gId);
              
              if (foundGateway) {
                if (!grpId) grpId = group.groupid || group.group_id || group.system_group_id || group.id;
                if (!sId) {
                  const sensors = Object.values(foundGateway).find(val => Array.isArray(val)) || [];
                  if (sensors.length > 0) {
                    sId = sensors[0].sensorid || sensors[0].sensor_id || sensors[0].id;
                  }
                }
                break;
              }
            }
          }
        }
        
        await store.dispatch('MyView/drillDown', {
          group: grpId,
          gateway: gId,
          sensor: sId
        });
      };

    const getMathCol = (row) => {
        return `${row.sensor_data_value ?? ''}`;
      };

    const datetimeConversion = (timestamp) => {
      if (!timestamp) return ''; // early return if timestamp is empty or null - fixes Dec 31 1969 display
      const utcDate = new Date(timestamp);
      return format(utcDate, 'MMM do yy, h:mm:ss a');
    };

    const goToMyView = () => {
      store.dispatch('MyView/resetView');
      // Home is not /gateways, so we always push
      router.push('/gateways');
    };

    return {
      goToMyView,
      bookmarkCurrentPage,
      lastCurrentPage,
      bookmarkPageSize,
      lastPageSize,
      commLoading,
      paginatedBookmarkedReadings,
      paginatedLastReadings,
      gatewayCount,
      click,
      getMathCol,
      format,
      currentCommUsage,
      lastReadings,
      bookmarkedReadings,
      bookmarkLoading,
      lastLoading,
      datetimeConversion
    }
  }
}
</script>
<style scoped>
  .card {
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
    border: 1px solid var(--color-border);
  }

  .card-header {
    font-weight: bold;
    font-size: 1.1rem;
    background-color: var(--color-sec);
    border-bottom: 2px solid var(--color-border);
    padding: 0;
  }
  .card-header span {
    padding: 8px 8px;
    border-radius: 5px 5px 0 0;
    background: var(--color-bg-80);
    box-shadow: 5px -1px 10px 0px rgba(0,0,0,0.3);
  }

  .table-hover tbody tr:hover,
  .reading-row:hover {
    background-color: var(--color-border);
    cursor: pointer;
  }

  .spinner_6KQt{animation:spinner_4IqM 2.2s cubic-bezier(0.52,.6,.25,.99) infinite}
  @keyframes spinner_4IqM {
    0% {
      transform:translate(12px,12px) scale(0);opacity:1
    }
    100% {
      transform:translate(0,0) scale(1);opacity:0
    }
  }

  /* Custom Table Styling (Tailwind-friendly replacement for Bootstrap) */
  .custom-table {
    width: 100%;
    border-collapse: collapse;
    border: none;
    color: inherit;
  }

  .custom-table thead th {
    position: sticky;
    top: 0;
    z-index: 10;
    background-color: var(--color-bg-80);
    box-shadow: inset 0 -2px 0 var(--color-border);
    border-left: none;
    border-right: 1px solid var(--color-border);
    color: var(--color-sec);
    text-transform: uppercase;
    font-size: 0.75rem;
    letter-spacing: 0.05em;
    padding: 0.75rem;
  }

  .custom-table tbody td {
    padding: 0.5rem;
    font-size: 14px;
    border-bottom: 1px solid var(--color-border);
    border-left: none;
    border-right: 1px solid var(--color-border);
  }

  .custom-table tbody tr:nth-of-type(even) {
    background-color: rgba(255, 255, 255, 0.02);
  }

  .reading-row {
    cursor: pointer;
  }
  .reading-row:hover > td {
    color: var(--color-sec);
  }
  .scrollable-table {
    overflow-y: auto;
  }
  
  /* Skeleton loader styles */
  .skeleton-loader {
    background-color: var(--color-border);
    animation: skeleton-loading 1.5s ease-in-out infinite;
  }
  
  @keyframes skeleton-loading {
    0% {
      background-color: var(--color-border);
    }
    50% {
      background-color: var(--color-bg-80);
    }
    100% {
      background-color: var(--color-border);
    }
  }
  
  /* Pagination controls styling */
  .btn-group-sm .btn {
    font-size: 0.75rem;
    padding: 0.25rem 0.5rem;
  }
  
  /* Improve table responsiveness */
  .table {
    font-size: 0.875rem;
  }
  
  .table th, .table td {
    padding: 0.5rem;
    vertical-align: middle;
  }
</style>
